import { DOCUMENT } from '@angular/common';
import { APP_INITIALIZER, DestroyRef, EnvironmentProviders, Inject, makeEnvironmentProviders } from '@angular/core';
import { getDestroySubjectFromRef } from '@bcf-v2-shared/misc/rx-helpers/get-destroy-subject-from-ref';
import { DEVICE_BASE_KIND } from '@bcf-v2-utilities/device-info/token';
import { updateWorkerToolkitFromUi } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/providers';
import { registerWorkerInstance } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/tokens';
import { domainNumberStrExtractor } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/domain-number-extractor';
import { BrowserSideMessage, initWorkerMessage } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/messages';
import { updateWorkerToolkitData } from '@bcf-vanilla-ts-v1-platforms/platform-worker/worker/bootstrap-worker';
import {
  WorkerToolkit,
  provideWorkerToolkit
} from '@bcf-vanilla-ts-v1-platforms/platform-worker/worker/worker-toolkit';
import { DeviceBaseType } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import { Subject, takeUntil } from 'rxjs';
import { DOMAIN_NUMBER_STR } from '../common/domain-number-str';
import { BrowserSideBlocController } from './browser-side-bloc-controller';
import { WorkerWrapper } from './worker-wrapper';
import { WorkerWrapperVirtual } from './worker-wrapper-virtual';

export function provideWorker(worker: Worker, appRuntimeId: number): EnvironmentProviders {
  registerWorkerInstance(worker);
  return makeEnvironmentProviders([
    {
      provide: DOMAIN_NUMBER_STR, // this is needed till we switch all requests to web-worker
      useFactory: (document: Document) => domainNumberStrExtractor(document.location.hostname),
      deps: [[new Inject(DOCUMENT)]]
    },
    {
      provide: WorkerWrapper,
      useFactory: () => new WorkerWrapper(worker)
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (document: Document, deviceKindBase: DeviceBaseType) => () => {
        worker.postMessage(initWorkerMessage(appRuntimeId, document.location.hostname));
        updateWorkerToolkitFromUi(worker, document, deviceKindBase);
      },
      deps: [[new Inject(DOCUMENT)], [new Inject(DEVICE_BASE_KIND)]],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (browserSideBlocController: BrowserSideBlocController<any>) => () => {
        browserSideBlocController.init();
      },
      deps: [BrowserSideBlocController],
      multi: true
    }
  ]);
}

export function provideWorkerVirtual(appRuntimeId: number): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: WorkerWrapper,
      useClass: WorkerWrapperVirtual
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (workerWrapper: WorkerWrapper, document: Document, deviceKindBase: DeviceBaseType, destroyRef: DestroyRef) =>
        () => {
          const toolkit: WorkerToolkit = provideWorkerToolkit();
          const destroy$: Subject<void> = getDestroySubjectFromRef(destroyRef);
          workerWrapper
            .getMessage<BrowserSideMessage>()
            .pipe(takeUntil(destroy$))
            .subscribe((message: BrowserSideMessage) => {
              updateWorkerToolkitData(message, toolkit);
            });
          workerWrapper.postMessage(initWorkerMessage(appRuntimeId, document.location.hostname));
          updateWorkerToolkitFromUi(workerWrapper, document, deviceKindBase);
        },
      deps: [WorkerWrapper, [new Inject(DOCUMENT)], [new Inject(DEVICE_BASE_KIND)], DestroyRef],
      multi: true
    }
  ]);
}
